<template>
    <PageLoading :loading="loading">
        <!-- 错误页面 -->
        <ErrorPage v-if="hasError" :title="errorTitle" :message="errorMessage" />

        <!-- 正常页面 -->
        <div v-else class="payment-page">
            <div class="payment-content">
                <!-- 订单金额 -->
                <section class="payment-section">
                    <div class="section-title">订单金额</div>
                    <div class="amount-row">
                        <span class="amount-label">总金额</span>
                        <span class="amount-value">¥{{ orderInfo.sale_price }}</span>
                    </div>
                </section>

                <!-- 支付明细 -->
                <section class="payment-section">
                    <div class="section-title">支付明细</div>
                    <div class="payment-methods">
                        <div class="payment-method" v-for="method in orderInfo.pay_arr" :key="method.name">
                            <div class="method-info">
                                <van-icon :name="paymentMethods.find(item => item.name === method.name)?.icon"
                                    :color="paymentMethods.find(item => item.name === method.name)?.iconColor"
                                    class="method-icon" />
                                <div class="method-details">
                                    <div class="method-name">{{ method.name }}</div>
                                    <div class="method-available" v-if="method.hasOwnProperty('deduction_amount')">抵扣{{
                                        method.deduction_amount }}积分</div>
                                    <div class="method-available"
                                        v-if="method.hasOwnProperty('number') && method.number > 0">抵扣{{
                                        method.number}}瓶</div>
                                </div>
                            </div>
                            <div class="method-amount">-¥{{ method.amount.toFixed(2) }}</div>
                        </div>
                    </div>
                </section>
            </div>

            <!-- 确认支付按钮 -->
            <div class="payment-footer">
                <van-button type="primary" block round class="confirm-btn" @click="confirmPayment">
                    确认支付
                </van-button>
            </div>
            <LoadingOverlay :show="isLoading" />
        </div>
    </PageLoading>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import { setWxTitle } from '@/utils/wx'
import { post } from '@/utils/request'
import { WECHAT_APPID } from '../../../config/env'
import { Dialog } from 'vant'
import LoadingOverlay from '@/components/LoadingOverlay.vue'

// 加载状态
const loading = ref(true)
const isLoading = ref(false)

// 错误状态
const hasError = ref(false)
const errorTitle = ref('')
const errorMessage = ref('')

// 订单信息
const orderInfo = ref({})
// 微信支付参数
const wechatPayParams = ref({
    type: '校园订奶',
    app_id: WECHAT_APPID,
    partner_id: parseInt(localStorage.getItem('userId')),
})
// 支付方式配置
const paymentMethods = ref([
    {
        type: 'points',
        name: '积分',
        icon: 'star',
        iconColor: '#FFD700',
    },
    {
        type: 'balance',
        name: '余额',
        icon: 'gold-coin-o',
        iconColor: '#8B0000',
    },
    {
        type: 'card',
        name: '卡',
        icon: 'card',
        iconColor: '#16A34A',
    },
    {
        type: 'wechat',
        name: '微信支付',
        icon: 'wechat',
        iconColor: '#07C160',
    },
    {
        type: 'other',
        name: '其他',
        icon: 'card',
        iconColor: '#07C160',
    },
])

// 处理错误
const handleError = (title, message) => {
    hasError.value = true
    errorTitle.value = title
    errorMessage.value = message
    loading.value = false
}

onMounted(() => {
    setWxTitle('订单支付')
    initPage()
})

// 页面初始化
const initPage = async () => {
    try {
        // 解码Base64数据
        const urlParams = new URLSearchParams(window.location.search)
        const encodedData = urlParams.get('data')

        if (!encodedData) {
            handleError('参数错误', '订单数据缺失，请重新选择套餐')
            return
        }

        const orderData = JSON.parse(decodeURIComponent(atob(encodedData)))
        console.log('订单数据:', orderData)

        // 这里可以调用接口获取支付相关信息
        const res = await post('/api/select/pay/by/term', {
            ...orderData,
            partner_id: parseInt(localStorage.getItem('userId')),
        })
        if (res.result && res.result.state === 'ok') {
            if (res.result.term_arr.length > 0) {
                orderInfo.value = res.result.term_arr[0]
                // 增加微信支付的
                orderInfo.value.pay_arr.push({
                    name: '微信支付',
                    amount: orderInfo.value.actual_amount,
                })
                const wechatData = {
                    student_id: orderData.student_id,
                    order_money: orderInfo.value.actual_amount,
                    device_id: orderData.device_id,
                    product_id: orderData.product_id,
                    order_cycle_id: orderData.term_id,
                    start_date: orderInfo.value.start_date,
                    end_date: orderInfo.value.end_date,
                    monday_categ_id: orderData.milk_types.find(item => item.week === '周一')?.id,
                    tuesday_categ_id: orderData.milk_types.find(item => item.week === '周二')?.id,
                    wednesday_categ_id: orderData.milk_types.find(item => item.week === '周三')?.id,
                    thursday_categ_id: orderData.milk_types.find(item => item.week === '周四')?.id,
                    friday_categ_id: orderData.milk_types.find(item => item.week === '周五')?.id,
                    saturday_categ_id: orderData.milk_types.find(item => item.week === '周六')?.id,
                    sunday_categ_id: orderData.milk_types.find(item => item.week === '周日')?.id,
                    is_face_collection: orderData.auth_face_recording,
                }
                wechatPayParams.value = {
                    ...wechatPayParams.value,
                    ...wechatData,
                }
            } else {
                handleError('获取支付信息失败', '接口返回周期数据为空')
            }
        } else {
            handleError('获取支付信息失败', res.result?.message || '接口异常')
        }
    } catch (error) {
        console.error('页面初始化失败:', error)
        handleError('数据解析失败', '订单数据格式错误，请重新选择套餐')
    } finally {
        loading.value = false
    }
}

// 确认支付
const confirmPayment = async () => {
    // 这里可以添加支付逻辑
    console.log('确认支付')
    isLoading.value = true
    // 支付成功后可以跳转到支付成功页面
    try {
        const res = await post('/api/create/community/wechat/order', {
            ...wechatPayParams.value,
        })
        if (res.result && res.result.state === 'ok') {
            if (wechatPayParams.value.order_money > 0) {
                // 调用微信支付
                WeixinJSBridge.invoke('getBrandWCPayRequest', {
                    "appId": res.result.appId,     //公众号ID，由商户传入     
                    "timeStamp": res.result.timeStamp,     //时间戳，自1970年以来的秒数     
                    "nonceStr": res.result.nonceStr,      //随机串     
                    "package": res.result.package,
                    "signType": res.result.signType,     //微信签名方式：     
                    "paySign": res.result.paySign //微信签名 
                },
                    function (res) {
                        if (res.err_msg == "get_brand_wcpay_request:ok") {
                            // 使用以上方式判断前端返回,微信团队郑重提示：
                            //res.err_msg将在用户支付成功后返回ok，但并不保证它绝对可靠，商户需进一步调用后端查单确认支付结果。
                            // this.$toast.success('订单提交成功')
                            window.location.href = '/my-orders'
                        }
                    });
            } else {
                // 支付成功后可以跳转到我的订单页面
                window.location.href = '/my-orders'
            }
        } else {
            Dialog.alert({
                title: '支付失败',
                message: res.result?.message || '接口异常'
            })
        }
    } catch (error) {
        console.error('确认支付失败:', error)
    } finally {
        isLoading.value = false
    }
}
</script>

<style scoped>
.payment-page {
    background: #fff;
    min-height: 100vh;
    padding-bottom: 100px;
    box-sizing: border-box;
}

.payment-section {
    margin: 0 14px 14px 14px;
    background: #fff;
    padding: 16px;
    border-radius: 8px;
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
}

.section-title {
    font-size: 16px;
    font-weight: 500;
    color: #333;
    margin-bottom: 16px;
}

.amount-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 0;
}

.amount-label {
    font-size: 14px;
    color: #666;
}

.amount-value {
    font-size: 18px;
    font-weight: 600;
    color: var(--van-primary-color);
}

.payment-methods {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.payment-method {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 0;
    border-bottom: 1px solid #f5f5f5;
}

.payment-method:last-child {
    border-bottom: none;
}

.method-info {
    display: flex;
    align-items: center;
    gap: 12px;
}

.method-icon {
    font-size: 20px;
}

.method-details {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.method-name {
    font-size: 14px;
    font-weight: 500;
    color: #333;
}

.method-available {
    font-size: 12px;
    color: #999;
}

.method-amount {
    font-size: 14px;
    font-weight: 500;
    color: var(--van-primary-color);
}

.payment-footer {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    background: #fff;
    border-top: 1px solid #eee;
    padding: 16px 14px;
    z-index: 1000;
}

.confirm-btn {
    background: var(--van-primary-color);
    border: none;
    height: 48px;
    font-size: 16px;
    font-weight: 500;
}

.confirm-btn:active {
    background: var(--van-primary-color);
}
</style>